// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Click-to-define for the jargon this tool cannot avoid.
 *
 * unfence is aimed partly at people who are building things without being
 * professional programmers — which is most of the point, since they are the ones
 * without an agent CLI. "Hunk", "unified diff" and "transient activation" are
 * all words the interface genuinely needs and none of them is common knowledge.
 */

export const TERMS: Record<string, string> = {
  hunk: 'One contiguous block of changes inside a file, with a few unchanged lines either side for context. A patch is made of hunks, and you can accept or reject each one on its own.',
  'unified diff':
    'The standard text format for describing a change to a file — the one with lines starting + and −. `git apply` reads it, and so does almost every code review tool.',
  'git apply':
    'A git command that takes a patch file and applies it to your working tree. Run it from the top level of your repository: `git apply changes.patch`.',
  elision:
    'When a model returns a file with its middle replaced by a comment such as `// ... rest unchanged`. Writing that over the real file destroys the part it left out.',
  gitignore:
    'A file listing which paths git should leave alone — build output, dependencies, secrets. unfence reads yours, including any nested ones, and leaves the same files out of the bundle.',
  'fenced code block':
    'A block of code in Markdown wrapped in triple backticks. A chat assistant returns each file as one of these.',
  'File System Access API':
    'The browser feature that lets a page read and write a folder you explicitly choose. Chromium-only: Mozilla and WebKit have both declined to implement the folder half.',
  bundle:
    'One Markdown file containing your whole project — a tree of what is in it, and every file in a labelled code block. This is what you paste into the chat window.',
  'context window':
    'How much text a model can consider at once, measured in tokens. A bundle bigger than the window is silently cut off, which is why unfence estimates the size before you copy it.',
  token:
    'The unit a model reads text in — roughly three and a half bytes of source code, though it varies by language. unfence estimates rather than counts, and shows a range.',
  backup:
    'A zip of every file that is about to be overwritten, written and verified before anything changes. If it cannot be written, nothing is.',
  patch: 'A text file describing changes, which you apply with `git apply`. See “unified diff”.',
};

let tooltip: HTMLElement | null = null;

function ensureTooltip(): HTMLElement {
  if (tooltip) return tooltip;
  tooltip = document.createElement('div');
  tooltip.className = 'glossary-tip';
  tooltip.setAttribute('role', 'tooltip');
  tooltip.hidden = true;
  document.body.appendChild(tooltip);
  return tooltip;
}

function hide(): void {
  if (tooltip) tooltip.hidden = true;
}

export function initGlossary(): void {
  const tip = ensureTooltip();

  document.addEventListener('pointerdown', (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('.glossary-link');
    if (!target) {
      // Outside-click dismissal, the same contract every overlay here follows.
      if (!tip.contains(event.target as Node)) hide();
      return;
    }
    const definition = TERMS[target.dataset.term ?? ''];
    if (!definition) return;
    event.preventDefault();

    tip.textContent = definition;
    tip.hidden = false;
    const rect = target.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    tip.style.width = `${width}px`;
    tip.style.left = `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`;
    // Above the term when there is no room below, so it never runs off a phone.
    const below = rect.bottom + 8;
    tip.style.top = below + tip.offsetHeight > window.innerHeight ? `${rect.top - tip.offsetHeight - 8}px` : `${below}px`;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hide();
  });
  window.addEventListener('resize', hide);
  window.addEventListener('scroll', hide, true);
}

/** Wrap a term in a clickable definition link. Returns a DOM node, never HTML. */
export function term(name: string, label = name): HTMLElement {
  const span = document.createElement('span');
  span.className = 'glossary-link';
  span.dataset.term = name;
  span.tabIndex = 0;
  span.textContent = label;
  return span;
}
